import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Button,
  HStack,
  InfoOutlineIcon,
  Placeholders,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tooltip,
  ViewIcon,
  ViewOffIcon,
} from 'src/ui'
import FuturesIndex from 'src/companies/trade/futures/futures-index'
import LocationIndex from 'src/companies/trade/location/location-index'
import SalesSeriesIndex from 'src/companies/trade/sales-series/sales-series-index'
import SsoUsersIndex from 'src/companies/trade/sso-users/sso-users-index'
import TenantConfigPanel from 'src/companies/configurations/tenant'
import MarketsConfig from './markets-config'
import PlatformMarketsConfig from './platform-markets-config'
import PlatformLocationsIndex from './platform-locations-index'
import PlatformCommodityMappings from './platform-commodity-mappings'
import CommoditySymbolMappings from './commodity-symbol-mappings'
import { useFindOrCreateTenantConfiguration } from 'src/api/queries/microservices/tenant-config'
import { Flex } from '@chakra-ui/react'
import { hasPermission } from 'src/utils/permissions'

const TradeTab = ({ index, label, currentIndex, isInactive = false, provider = '' }) => (
  <Tab
    paddingBottom={2}
    borderBottom={`3px solid ${currentIndex === index ? '#2a69ac' : 'transparent'}`}
    color={isInactive ? 'gray.500' : undefined}
    fontStyle={isInactive ? 'italic' : undefined}
  >
    <Tooltip
      label={`${provider} is not the current data provider for this tenant.`}
      placement="top"
      isDisabled={!isInactive}
    >
      <HStack spacing={1}>
        {isInactive && <InfoOutlineIcon boxSize={3} color="orange.400" />}
        <span>{label}</span>
      </HStack>
    </Tooltip>
  </Tab>
)

const MarketsTabs = () => {
  const { data: tenantConfig, status: tenantConfigStatus } = useFindOrCreateTenantConfiguration(
    '/api/markets/aggregator/config/v1'
  )
  const canViewSsoUsers = hasPermission('sso_cqg_users_view')

  const provider = tenantConfig?.find((config) => config.key === 'provider')?.value as
    | undefined
    | 'None'
    | 'Bushel Trade'
    | 'Centre 1.0.0'
    | 'Centre 1.1.0'
    | 'Platform Markets Service'

  const [showAllTabs, setShowAllTabs] = useState(false)

  const allTabs = useMemo(
    () =>
      [
        {
          label: 'Configuration',
          panel: <MarketsConfig />,
        },
        {
          label: 'Futures',
          panel: <FuturesIndex />,
        },
        {
          label: 'Commodity',
          panel: <CommoditySymbolMappings />,
          provider: 'Bushel Trade',
        },
        {
          label: 'Tenant',
          panel: (
            <TenantConfigPanel
              baseUri="/api/trade/config/v1/operation"
              editPermission="offer_management_edit"
              alwaysExpanded
            />
          ),
          provider: 'Bushel Trade',
        },
        {
          label: 'Platform',
          panel: <PlatformMarketsConfig />,
          provider: 'Platform Markets Service',
        },
        {
          label: 'Platform Locations',
          panel: <PlatformLocationsIndex />,
          provider: 'Platform Markets Service',
        },
        {
          label: 'Platform Commodities',
          panel: <PlatformCommodityMappings />,
          provider: 'Platform Markets Service',
        },
        {
          label: 'Locations',
          panel: <LocationIndex />,
          provider: 'Bushel Trade',
        },
        {
          label: 'SSO Users',
          panel: <SsoUsersIndex />,
          provider: 'Bushel Trade',
          hidden: !canViewSsoUsers,
        },
        {
          label: 'Sales Series',
          panel: <SalesSeriesIndex />,
          provider: 'Bushel Trade',
        },
      ]
        .filter((tab) => !tab.hidden)
        .map((tab) => ({ ...tab, isInactive: tab.provider && tab.provider !== provider })),
    [canViewSsoUsers, provider]
  )

  const tabs = allTabs.filter((tab) => showAllTabs || !tab.isInactive)
  const hasHiddenTabs = allTabs.some((tab) => tab.isInactive)

  useEffect(() => {
    const [firstHash] = window.location.hash?.slice(1).split('&')
    const index = tabs.findIndex(({ label }) => label === decodeURI(firstHash))
    if (index !== -1) {
      setTabIndex(index)
    }
  }, [tabs])

  const [tabIndex, setTabIndex] = React.useState(0)

  const handleTabClick = useCallback((newIndex, newLabel) => {
    setTabIndex(newIndex)
    window.history.replaceState(null, '', `#${encodeURIComponent(newLabel)}`)
  }, [])

  if (tenantConfigStatus === 'error') {
    return <Placeholders.FailedState mt={8} />
  }
  if (tenantConfigStatus === 'pending') {
    return <Placeholders.LoadingState mt={8} />
  }

  return (
    <Tabs
      size="sm"
      index={tabIndex}
      onChange={(index) => {
        handleTabClick(index, tabs[index].label)
      }}
    >
      <Flex justifyContent="space-between" alignItems="center">
        <TabList>
          {tabs.map(({ label, isInactive, provider }, index) => (
            <TradeTab
              key={`tab-${label}`}
              index={index}
              label={label}
              currentIndex={tabIndex}
              isInactive={isInactive}
              provider={provider}
            />
          ))}
        </TabList>
        {hasHiddenTabs && (
          <Button
            size="sm"
            variant="ghost"
            leftIcon={showAllTabs ? <ViewOffIcon /> : <ViewIcon />}
            onClick={() => setShowAllTabs(!showAllTabs)}
          >
            {showAllTabs ? 'Hide inactive' : 'Show all'}
          </Button>
        )}
      </Flex>

      <TabPanels mt={3}>
        {tabs.map(({ label, panel }) => (
          <TabPanel key={`panel-${label}`} p={0}>
            {panel}
          </TabPanel>
        ))}
      </TabPanels>
    </Tabs>
  )
}

export default MarketsTabs
